import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip, notification } from 'antd';
import { ReloadOutlined, CrownOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { getBillerClinics } from '../../../../Apis/Biller';
import { AddUser } from '../../../../Redux/Actions/User';

const SwitcherWrapper = styled.div`
  border-top: 1px solid #f0f0f0;
  padding: 12px 8px 8px;
`;

const SwitcherHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.textColor || '#333'};
  margin-bottom: 6px;
  padding: 0 4px;
`;

const RefreshBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #aaa;
  padding: 2px;
  display: flex;
  align-items: center;
  &:hover { color: #1890ff; }
`;

const ClinicItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 400;
  color: #666;
  background: #fafafa;
  margin-bottom: 4px;
  overflow: hidden;
`;

const ClinicName = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const AdminBadge = styled.span`
  font-size: 10px;
  color: #faad14;
  flex-shrink: 0;
`;

const ClinicSwitcher = ({ collapsed }) => {
  const user = useSelector(state => state.userReducer);
  const [theme] = useSelector(state => [state.themeReducer]);
  const dispatch = useDispatch();
  const textColor = theme?.['@text-color'] || '#333';
  const [refreshing, setRefreshing] = useState(false);

  if (user.userType !== 'biller') return null;
  const clinics = user.clinics || [];
  if (clinics.length <= 1) return null;

  const handleRefresh = () => {
    setRefreshing(true);
    getBillerClinics()
      .then(response => {
        if (response.data.success) {
          dispatch(AddUser({ ...user, clinics: response.data.result }));
        } else {
          notification.error({ message: response.data.message });
        }
      })
      .catch(() => notification.error({ message: 'Failed to refresh clinics' }))
      .finally(() => setRefreshing(false));
  };

  if (collapsed) {
    return (
      <SwitcherWrapper style={{ padding: '12px 4px 8px' }}>
        {clinics.map(clinic => (
          <Tooltip key={clinic.clinicId} title={clinic.clinicName || clinic.clinicId} placement='right'>
            <ClinicItem textColor={textColor} style={{ justifyContent: 'center', padding: '5px 4px' }}>
              {clinic.billerAdmin
                ? <CrownOutlined style={{ color: '#faad14' }} />
                : <span style={{ width: 14, height: 14, borderRadius: '50%', background: '#d9d9d9', display: 'inline-block' }} />}
            </ClinicItem>
          </Tooltip>
        ))}
      </SwitcherWrapper>
    );
  }

  return (
    <SwitcherWrapper>
      <SwitcherHeader textColor={textColor}>
        <span>My Clinics</span>
        <Tooltip title='Refresh'>
          <RefreshBtn onClick={handleRefresh}>
            <ReloadOutlined spin={refreshing} />
          </RefreshBtn>
        </Tooltip>
      </SwitcherHeader>
      {clinics.map(clinic => (
        <ClinicItem key={clinic.clinicId} textColor={textColor}>
          <ClinicName title={clinic.clinicName || clinic.clinicId}>
            {clinic.clinicName || clinic.clinicId}
          </ClinicName>
          {clinic.billerAdmin && (
            <Tooltip title='Biller Admin for this clinic'>
              <AdminBadge><CrownOutlined /></AdminBadge>
            </Tooltip>
          )}
        </ClinicItem>
      ))}
    </SwitcherWrapper>
  );
};

export default ClinicSwitcher;
